import { baseService } from "../base";
import { ServiceForInvoice, Invoice } from "~/components/admin/billing/types";

// Types for billing API
export interface CreateInvoiceRequest {
  clientId: string;
  serviceTicketIds?: string[];
  invoiceDate?: string;
  dueDate?: string;
  notes?: string;
  subtotal: number;
  tax: number;
  total: number;
  lineItems?: any[];
}

export interface GetServicesRequest {
  bookingId?: string;
  clientId?: string;
  status?: "draft" | "pending" | "sent" | "paid" | "overdue" | "cancelled";
}

export interface GetInvoicesRequest {
  clientId?: string;
  status?: "draft" | "pending" | "sent" | "paid" | "overdue" | "cancelled";
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface BillingDashboardData {
  totalPending: number;
  totalInvoices: number;
  totalPaid: number;
  totalOutstanding: number;
  recentInvoices: Invoice[];
}

// Shared billing interfaces
export interface BillingService {
  id: string;
  serviceType: string;
  serviceReference: string;
  serviceDate: string;
  totalHours: number;
  staffNames: string[];
  staffRates: number[];
  serviceCharges: number;
  laborCharges: number;
  supplyCharges: number;
  totalAmount: number;
  inventoryCubicFootage: number;
  invoiceStatus: string;
  description: string;
  quantity: number;
  rate: number;
  suppliesUsed: Array<{
    id: string;
    name: string;
    quantity: number;
    cost: number;
    price: number;
    selected: boolean;
  }>;
  calculationDetails?: {
    staffCosts?: Array<{
      staffRoleName: string;
      hourlyRate: number;
      quantity: number;
      cost: number;
    }>;
    hoursWorked?: number;
    vesselCost?: number;
    totalHours?: number;
    travelTime?: number;
    siteTime?: number;
    combinedHourlyRate?: number;
  };
}

export interface BookingBillingData {
  booking: {
    id: string;
    bookingReference: string;
    client: {
      id: string;
      companyName: string;
    };
    location: {
      id: string;
      name: string;
    };
  };
  services: BillingService[];
  summary: {
    totalServices: number;
    pendingServices: number;
    invoicedServices: number;
    totalAmount: number;
  };
}

// Get services for invoicing
export const getServicesForInvoice = async (
  request: GetServicesRequest
): Promise<ServiceForInvoice[]> => {
  const { data } = await baseService.get("/billing/services", {
    params: request,
  });

  return data;
};

// Create invoice
export const createInvoice = async (
  request: CreateInvoiceRequest & { bookingId?: string; invoiceNumber?: string }
): Promise<{ success: boolean; invoiceId: string; invoiceNumber?: string; message?: string }> => {
  const { data } = await baseService.post("/billing", request);

  return {
    success: true,
    invoiceId: data.id,
    invoiceNumber: data.invoiceNumber,
    message: "Invoice created successfully",
  };
};

// Get invoices
export const getInvoices = async (
  request: GetInvoicesRequest
): Promise<{ invoices: Invoice[]; total: number }> => {
  const { data } = await baseService.get("/billing", {
    params: request,
  });

  // Map backend fields to frontend expected fields
  const mappedInvoices = data.data.map((billing: any) => ({
    id: billing.id,
    invoiceNumber: billing.invoiceNumber,
    client: billing.client?.companyName || "N/A",
    amount: Number(billing.total) || 0,
    status: billing.status,
    date: billing.invoiceDate || billing.createdAt,
    stripePaymentLinkUrl: billing.stripePaymentLinkUrl,
  }));

  return {
    invoices: mappedInvoices,
    total: data.total,
  };
};

// Get billing dashboard data
export const getBillingDashboardData = async (
  startDate?: string,
  endDate?: string
): Promise<BillingDashboardData> => {
  const { data } = await baseService.get("/billing/dashboard", {
    params: { startDate, endDate },
  });

  // Map recent invoices to expected format
  const mappedRecentInvoices = data.recentInvoices.map((billing: any) => ({
    id: billing.id,
    invoiceNumber: billing.invoiceNumber,
    client: billing.client?.companyName || "N/A",
    amount: Number(billing.total) || 0,
    status: billing.status,
    date: billing.invoiceDate || billing.createdAt,
    stripePaymentLinkUrl: billing.stripePaymentLinkUrl,
  }));

  return {
    ...data,
    recentInvoices: mappedRecentInvoices,
  };
};

// Get single invoice
export const getInvoice = async (id: string): Promise<any> => {
  const { data } = await baseService.get(`/billing/${id}`);
  return data;
};

// Get invoices for a booking
export const getInvoicesForBooking = async (bookingId: string): Promise<any[]> => {
  const { data } = await baseService.get(`/billing/booking/${bookingId}`);
  return data;
};

// Update invoice
export const updateInvoice = async (
  id: string,
  updateData: any
): Promise<any> => {
  const { data } = await baseService.patch(`/billing/${id}`, updateData);
  return data;
};

// Mark invoice as paid
export const markInvoiceAsPaid = async (
  id: string,
  paymentMethod: string,
  paidAmount?: number
): Promise<any> => {
  const { data } = await baseService.patch(`/billing/${id}/mark-paid`, {
    paymentMethod,
    paidAmount,
  });
  return data;
};

// Send invoice
export const sendInvoice = async (id: string): Promise<any> => {
  const { data } = await baseService.patch(`/billing/${id}/send`);
  return data;
};

// Delete invoice
export const deleteInvoice = async (id: string): Promise<void> => {
  await baseService.delete(`/billing/${id}`);
};

// Bulk send invoices (marks them as sent)
export const bulkSendInvoices = async (
  ids: string[],
  exportFilename?: string
): Promise<{
  success: any[];
  failed: { id: string; error: string }[];
}> => {
  const { data } = await baseService.post("/billing/bulk/send", {
    ids,
    exportFilename,
  });
  return data;
};

// Get full invoice data for export
export const getInvoicesForExport = async (ids: string[]): Promise<any[]> => {
  const { data } = await baseService.post("/billing/bulk/export", { ids });
  return data;
};

// Get invoice PDF (generated locally, not from external service)
export const getInvoicePdf = async (id: string): Promise<Blob> => {
  const response = await baseService.get(`/billing/${id}/pdf`, {
    responseType: 'blob'
  });
  return response.data;
};

// Stripe Connect
export const startStripeConnect = async (): Promise<{ url: string }> => {
  const { data } = await baseService.post('/stripe/connect/onboard');
  return data;
};

export const getStripeConnectStatus = async (): Promise<{
  connected: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  accountId: string | null;
}> => {
  const { data } = await baseService.get('/stripe/connect/status');
  return data;
};

export const refreshStripeConnectLink = async (): Promise<{ url: string }> => {
  const { data } = await baseService.post('/stripe/connect/refresh-link');
  return data;
};

// Stripe payment link
export const createPaymentLink = async (
  billingId: string
): Promise<{ paymentLinkId: string; paymentLinkUrl: string }> => {
  const { data } = await baseService.post(`/stripe/payment-link/${billingId}`);
  return data;
};

export const getPaymentLink = async (
  billingId: string
): Promise<{ paymentLinkUrl: string | null }> => {
  const { data } = await baseService.get(`/stripe/payment-link/${billingId}`);
  return data;
};

// Shared billing API service
export const billingApi = {
  // Get billing data for a booking (used by both booking detail and booking list)
  async getBookingBillingData(bookingId: string): Promise<BookingBillingData> {
    const response = await baseService.get(
      `/billing/booking/${bookingId}/billing-data`
    );
    return response.data;
  },

  // Synchronize billing calculation data between API and UI
  synchronizeBillingCalculation(bookingId: string): Promise<BookingBillingData> {
    return new Promise<BookingBillingData>((resolve, reject) => {
      baseService.get(`/billing/booking/${bookingId}/billing-data`)
        .then(response => {
          const billingData = response.data as BookingBillingData;

          // Ensure calculation values are correct
          if (billingData && billingData.services) {
            billingData.services = billingData.services.map((service: BillingService) => {
              // Ensure totalAmount = quantity * rate
              if (service.quantity && service.rate) {
                service.totalAmount = service.quantity * service.rate;
              }
              return service;
            });

            // Update total amount in summary
            if (billingData.summary) {
              billingData.summary.totalAmount = billingData.services.reduce(
                (sum: number, service: BillingService) => sum + (service.totalAmount || 0),
                0
              );
            }
          }

          resolve(billingData);
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  // Calculate line items for invoice creation
  calculateLineItems(services: BillingService[]): Array<{
    description: string;
    quantity: number;
    unitPrice: number;
  }> {
    return services.map((service) => ({
      description: service.description,
      quantity: service.quantity,
      unitPrice: service.rate,
    }));
  },

  // Calculate totals from billing services
  calculateTotals(
    services: BillingService[],
    bookingId?: string,
    applyTax: boolean = false
  ) {
    const subtotal = services.reduce(
      (sum, service) => sum + (service.totalAmount || 0),
      0
    );
    const taxRate = 0.08875; // 8.875% like booking detail

    // If applyTax is false, return subtotal without tax
    if (!applyTax) {
      return {
        subtotal,
        tax: 0,
        total: subtotal,
      };
    }

    // Calculate taxable amount based on service types
    // Delivery and Collection are untaxed, Storage is taxed
    const taxableAmount = services.reduce((sum, service) => {
      if (
        service.serviceType === "COLLECTION" ||
        service.serviceType === "DELIVERY"
      ) {
        // Collection and Delivery are untaxed
        return sum;
      } else if (service.serviceType === "WAREHOUSE_SERVICE") {
        // Storage is taxed
        return sum + (service.totalAmount || 0);
      } else {
        // Other services (ON_SITE_SERVICE, FABRICATION_SERVICE, etc.) are taxed
        return sum + (service.totalAmount || 0);
      }
    }, 0);

    const tax = taxableAmount * taxRate;
    const total = subtotal + tax;

    return {
      subtotal,
      tax,
      total,
    };
  },

  // Format currency
  formatCurrency(amount: number): string {
    return `$${amount.toFixed(2)}`;
  },

  // Group services by type for display
  groupServicesByType(
    services: BillingService[]
  ): Record<string, BillingService[]> {
    return services.reduce((groups, service) => {
      const type = service.serviceType;
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(service);
      return groups;
    }, {} as Record<string, BillingService[]>);
  },

  // Convert job tickets to BookingForInvoice format
  formatJobTicketsForInvoice(jobTickets: any[]): any[] {
    return jobTickets.map((jobTicket) => ({
      id: jobTicket.id,
      referenceNumber: jobTicket.referenceNumber,
      client: {
        companyName: jobTicket.client?.companyName || "Unknown Client",
      },
      project: {
        name: jobTicket.project?.name || "No Project",
      },
      status: jobTicket.status || "SCHEDULED",
      createdAt: jobTicket.createdAt,
      serviceTicket: jobTicket.serviceTicket || [],
    }));
  },
};
